import { useEffect, useRef, useState } from "react";
import CustomStyledTable from "../../../../components/table/CustomStyledTable";
import { GridActionsCellItem } from "@mui/x-data-grid";
import { BiDotsHorizontalRounded } from "react-icons/bi";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	useDeleteGrievanceReport,
	useGetGrievanceReport,
	useGetGrievanceReportBySearch,
} from "../../../../services/grievance-report.service";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import ActionPopover from "../../../../components/popovers/actionPopover";
import { GrievanceReportDto } from "../../../../models/grievance-report.dto";
import { AiOutlinePlus } from "react-icons/ai";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { APP_CONFIG, CustomizedTooltip } from "../../../../helpers/appHelpers";
import { Typography } from "@mui/material";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetGrievanceType } from "../../../../services/grievance-type.service";
import GrievanceModalForm from "../modals/GrievanceModalForm";
import APISearchInput from "../../../../components/search/APISearchInput";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import {
	grievanceReportsColumns,
	grievanceReportsColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { useTableConfiguration } from "../../../../helpers/useTableConfiguration";
import { TableId } from "../../../../models/table-configuration.dto";
import useUserStore from "../../../../state-management/useUserStore";
import { grievanceColumns } from "../../../payroll_company/company_settings/views/grievance_types/tanstack-columns";
import ExportGrievanceReportsModal from "../modals/ExportGrievanceReportsModal";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import { ChevronDown, Upload } from "lucide-react";
import {
	PERMISSIONS,
	useCheckUserPermissions,
} from "../../../../helpers/usePermissionManager";

const GrievanceReportsView = () => {
	const { currentCompany } = useCompanyStore();
	const { currentUser, userType } = useUserStore();
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const { tableKey, columnVisibilityModel, setColumnVisibilityModel } =
		useColumnVisibilityModel(grievanceReportsColumnVisibility);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement | null>(null);
	const { checkUserPermissionsOr } = useCheckUserPermissions();
	// Conduct managers may manage any grievance report; other users only their own
	// Same rule as payroll-hr: HR or Operations users with the conduct permission
	const canManageConduct =
		["HR", "OPERATIONS"].includes(currentUser?.user?.category as string) &&
		checkUserPermissionsOr([PERMISSIONS.COMPANY_CONFIGS_CONDUCT_WRITE]);
	// The employee payroll-hr treats as this user: its auth token carries the
	// first company profile's employee, whichever company is in view
	const ownEmployeeId = currentUser.user.companyProfiles?.[0]?.employeeId;
	const canManageReport = (report?: GrievanceReportDto) =>
		canManageConduct ||
		(!!ownEmployeeId && report?.reportingEmployeeId === ownEmployeeId);
	// Non-managers can only file reports as themselves, so need an employee profile
	const canAddReport = canManageConduct || !!ownEmployeeId;

	const { mutate: searchGrievanceReports } = useGetGrievanceReportBySearch();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<GrievanceReportDto>({
		fetchHook: useGetGrievanceReport,
		options: { companyId: currentCompany?.id as number },
	});

	// Fetched data for select fields requiring data from database
	const options = {
		companyId: currentCompany?.id as number,
		limit: APP_CONFIG.PAGE_LIMIT,
		page: 1,
		orderBy: "createdAt:desc" as any,
	};
	const { data: employees } = useGetEmployee({
		...options,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});
	const { data: grievanceTypes } = useGetGrievanceType(options);

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteGrievanceReport } = useDeleteGrievanceReport();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteGrievanceReport(
			{ id: formDataValues?.id },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	const handleOpenExportModal = () => {
		setIsOpen(false);
		setOpenExportModal(true);
	};

	const additionalColumns = [
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			minWidth: 150,
			flex: 1,
			headerClassName: "super-app-theme--header",
			cellClassName: "actions ",
			getActions: (params: any) => {
				return [
					<GridActionsCellItem
						icon={<BiDotsHorizontalRounded />}
						label="actions"
						onClick={(e: any) => {
							// console.log(params.row, "params");
							setFormData(params?.row);
							setAnchorEl(e.currentTarget);
						}}
					/>,
				];
			},
		},
	];

	return (
		<>
			<div className="flex justify-between items-center mt-4 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchGrievanceReports}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{
										title: "Report Number",
										fieldName: "reportNumber",
									},
									{
										title: "Report Date",
										fieldName: "reportDate",
									},
									{
										title: "Date Created",
										fieldName: "createdAt",
									},
									{
										title: "Date Modified",
										fieldName: "modifiedAt",
									},
								],
							},
							{
								title: "Grievance Type",
								fieldName: "grievanceTypeId",
								fieldType: "SELECT_FIELD",
								options: grievanceTypes?.data || [],
							},
							{
								title: "Reporting Employee",
								fieldName: "reportingEmployeeId",
								fieldType: "SELECT_FIELD",
								options: employees?.data || [],
								mapTexts: ["firstName", "lastName"],
							},
							{
								title: "Report Date",
								fieldName: "reportDate",
								fieldType: "INPUT_FIELD",
								inputType: "date",
							},
							{
								title: "Date Created",
								fieldName: "createdAt",
								fieldType: "DATE_RANGE",
							},
						]}
					/>
					<ManageTableColumns
						columns={grievanceColumns}
						defaultVisibilityModel={grievanceReportsColumnVisibility}
						visibilityModel={columnVisibilityModel}
						setVisibilityModel={setColumnVisibilityModel}
						tableApiRef={tableAPIRef}
					/>
				</div>
				<div className="flex items-center mx-1">
					<div className="relative" ref={dropdownRef}>
						{/* Dropdown Trigger Button */}
						<button
							onClick={() => setIsOpen(!isOpen)}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<Upload className="mr-3 text-[17px] text-gray-500" />
							Upload/Export
							<ChevronDown
								className={`ml-2 text-[14px] text-gray-500 transition-transform duration-200 ${
									isOpen ? "rotate-180" : ""
								}`}
							/>
						</button>

						{/* Dropdown Menu */}
						{isOpen && (
							<div className="absolute top-full left-0 mt-2 w-full min-w-[180px] bg-white border border-gray-200 rounded-xl shadow-lg z-50">
								<div className="p-2 space-y-2">
									{/* <PermissionChecker
														permissionRule="OR"
														permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
													>
														<button
															onClick={() => setOpenUploadSalaryAdvancesModal(true)}
															className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
																	   border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
														>
															<GoUpload className="text-[18px] text-gray-500" />
															Upload Salary Advance
														</button>
													</PermissionChecker> */}
									<button
										onClick={handleOpenExportModal}
										className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 bg-gray-50 
																	 border border-gray-200 rounded-lg transition hover:bg-gray-100 hover:shadow-sm"
									>
										<BsDownload className="text-[18px] text-gray-500" />
										Export Grievance Reports
									</button>
								</div>
							</div>
						)}
					</div>
				</div>
				{canAddReport ? (
					<div
						className="flex items-center text-primary-blue cursor-pointer"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-base" />
						<span className="text-sm">Add New Grievance Report</span>
					</div>
				) : (
					<CustomizedTooltip
						title="You need an employee profile in this company to file a grievance report"
						placement="top"
						arrow
					>
						<Typography>
							<button
								type="button"
								className="w-fit h-fit cursor-not-allowed failed-permission-check"
								disabled
							>
								<div className="flex items-center text-primary-blue">
									<AiOutlinePlus className="mr-1 text-base" />
									<span className="text-sm">Add New Grievance Report</span>
								</div>
							</button>
						</Typography>
					</CustomizedTooltip>
				)}
			</div>
			<TanStackEmployeeTable
				ref={tableAPIRef}
				tableId={TableId.GRIEVANCES}
				data={tableData}
				columns={grievanceReportsColumns}
				columnVisibility={columnVisibilityModel} // ✅ Add this
				onColumnVisibilityChange={setColumnVisibilityModel} // ✅ Add this
				loading={isLoading}
				// Rows the user can't change aren't clickable (no pointer cursor)
				conditionalRowAction={canManageReport}
				onRowClick={(row, event) => {
					if (!canManageReport(row)) return;
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					if (!canManageReport(row)) return;
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={grievanceReportsColumnVisibility}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal && canManageReport(formDataValues)}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal &&
				(isEditForm ? canManageReport(formDataValues) : canAddReport) && (
				<GrievanceModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm ? "Update Grievance Report" : "New Grievance Report"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					employees={employees?.data || []}
					grievanceTypes={grievanceTypes?.data || []}
					reloadTableData={reloadTableData}
					canManageConduct={canManageConduct}
					ownEmployeeId={ownEmployeeId}
				/>
			)}
		</>
	);
};
export default GrievanceReportsView;
